import {action, autorun, makeAutoObservable, set, remove, observable, get, has} from "mobx";

import WatsonApi from "../backends/WatsonApi";
import deepmerge from 'deepmerge'
import {convertFlatToTree} from "../utils/DataUtilities";
import {defaultJsonData} from "../utils/Settings";
import {isJson} from "../components/admin/spinup/utilities";
import {getConfig} from "../_configs/AppConfig";
import {ExecutionEnvironment} from "./InterfaceStore";

const {publicRuntimeConfig} = getConfig();

export function mergeBlockItems(oldItems, newItems) {
    return [...oldItems, ...newItems].reduce((acc, current) => {
        if (!acc.find(c => c.id === current.id)) {
            acc.push(current);
        }
        return acc;
    }, [])
}

export const blockLookups = {
    news: ['is_news'],
    events: ['is_event'],
    media: ['is_media'],
    files: ['is_file', 'is_folder'],
};

export enum fetchState {
    "PENDING SCHEMA",
    "PENDING RESPONSE",
    "SUCCESS",
    "FAILURE",
}

export class FetchState {
    public value;
    public message;

    constructor({value, message}: {value: fetchState, message: string}) {
        this.value = value;
        this.message = message;
        makeAutoObservable(this);
    }
    set({value, message}: {value: fetchState, message: string}) {
        this.value = value;
        this.message = message;
    }
}

export default class OrganizationStore {
    private userId = null;

    constructor(userStore) {
        if (ExecutionEnvironment.canUseDOM) { // don't leak memory!
            autorun(() => {
                this.setUserId(userStore.id)
            });
        }

        makeAutoObservable(this, {
            updateThemeSettings: action,
        });
    }

    setUserId = id => {
        this.userId = id;
    };

    organization: OrganizationTypeOrganization | Partial<OrganizationTypeOrganization> = {}; // title organization
    currentOrganization: OrganizationTypeOrganization | Partial<OrganizationTypeOrganization> = {};
    locations: LocationContentItem[] = [];
    _navigation: Navigation[] = [];

    get hero() {
        return this.organization?.json_data?.settings.hero.map(h => {
            if (!/^(http|https):\/\//i.test(h.media_url)) {
                return {
                    ...h,
                    media_url: publicRuntimeConfig.CDN_URL + "/" + h.media_url,
                };
            }
            return h;
        }) || [];
    }

    content_sources: ContentSource[] = [];
    children: OrganizationTypeOrganization[] = []; // list of pages or collections whose parents are current organization
    hostname = null;

    customNavigationIsDisabled = true;

    content_analytics = {};
    userSites = [];

    get navigation() {
        return this._navigation;
    }

    set navigation(flatArray) {
        this._navigation = flatArray;
    }

    get shouldUpdateSources() {
        return this.content_analytics &&
            Array.from(Object.keys(this.content_analytics))
                .filter(key => !['-1', 'total', 'published_total', 'unpublished_total', 'user_permissions'].includes(key))
                .length !== this.content_sources.filter(source => !source.state.includes('error')).length;
    }

    get navigationTree() {
        return convertFlatToTree(this._navigation);
    }

    setCurrentOrganization = (org: OrganizationTypeOrganization) => {
        this.currentOrganization = org;
    }

    updateThemeSettings = (obj, newThemeID = false) => {
        if (newThemeID && this.organization.json_data) {
            this.organization.json_data.settings.appearance.themeID = String(newThemeID);
        }
        const themeID = this.organization.json_data?.settings.appearance.themeID;
        if (themeID) {
            for (const k in obj) {
                if (obj.hasOwnProperty(k) && this.organization.json_data) {
                    this.organization.json_data.settings.appearance.themes[themeID][k] = obj[k];
                }
            }
        }
    };

    fetchStates = observable.map();

    removeFetchState(requestId) {
        if (requestId) {
            remove(this.fetchStates, requestId);
        }
    }

    updateFetchState(requestId, state) {
        if (requestId) {
            if (has(this.fetchStates, requestId)) {
                get(this.fetchStates, requestId).set(state);
            } else {
                set(this.fetchStates, requestId, new FetchState(state));
            }
        }
    }

    async fetchClient (tag, fn, args = {}, options: {
        requestId?: string,
        requestSuccessMessage?: string,
        organization_id?: OrganizationTypeOrganization["id"],
        user_id?: string,
        spinup?: boolean,
    } = {}) {
        const {
            requestId = null, // ID for tracking progress of request
            requestSuccessMessage = "Beep, boop... all done, your site has been updated!",
            organization_id = this.currentOrganization?.id || this.organization.id,
            user_id = this.userId,
            spinup = false,
        } = options;

        try {
            this.updateFetchState(requestId, {value: fetchState["PENDING SCHEMA"]});
            const client = await WatsonApi();
            this.updateFetchState(requestId, {value: fetchState["PENDING RESPONSE"]});

            const res = await client[tag][fn](args);
            this.updateFetchState(requestId, {value: fetchState.SUCCESS, message: requestSuccessMessage});
            return res;
        } catch (error) {
            let message = "";
            if (typeof error === 'string') {
                message = `Oops! ${error}`;
            } else if (isJson(error)) {
                message = `Oops! ${JSON.parse(error.statusText).detail}`
            } else if (isJson(error?.data)) {
                message = JSON.parse(error.data).detail;
            }
            this.updateFetchState(requestId, {value: fetchState.FAILURE, message});
        } finally {
            this.removeFetchState(requestId);
        }
    }

    deleteNavigation = async (node: Navigation) => {
        const includeAllChildren = this.navigation.filter(item => {
            return item.path.slice(0, node.path.length) === node.path;
        }).map(item => item.id);
        // use something like ^^ for getting all children and bulk deleting
        await this.fetchClient("organizations", "organizations_navigations_bulk_destroy", {
            organization_pk: this.organization.id,
            data: includeAllChildren,
        });
        this.navigation = this._navigation.filter(item => !includeAllChildren.includes(item.id));
    };

    deleteContentSource = function* (id) {
        try {
            yield this.fetchClient("organizations", "organizations_content_sources_delete", {
                organization_pk: this.organization.id,
                id: id,
            });
            this.content_sources = this.content_sources.filter(item => item.id !== id);
        } catch (error) {
            console.debug(error);
        }
    }

    updateContentSource = function* (id, url) {
        yield this.fetchClient("organizations", "organizations_content_sources_partial_update", {
            organization_pk: this.organization.id,
            id: id,
            data: {
                "source_url": url,
            },
        });
        this.content_sources = this.content_sources.map(item => {
            if (item.id === id) {
                return {
                    ...item,
                    source_url: url,
                }
            }
            return item;
        });
    }

    buildOrganization(newOrg: OrganizationTypeOrganization) {
        let titleOrg = newOrg;

        if (titleOrg.categories && titleOrg.categories.length > 0) {
            newOrg.vertical = titleOrg.categories[0].path;
        } else {
            newOrg.vertical = 'generic';
        }

        const defaultSettings = defaultJsonData(newOrg.vertical);
        titleOrg.json_data = deepmerge(defaultSettings, titleOrg.json_data);

        if (titleOrg.navigation && Array.isArray(titleOrg.navigation)) {
            // this map can be removed once we're totally on Watson, but for now there are some discrepancies between
            // the model field names of Yii and Watson, so this is here to keep things consistent
            this.navigation = titleOrg.navigation;
        }

        // hero should be an array, let's make it backwards compatible in case any objects or strings still exist
        if (titleOrg.json_data.settings.hero && !Array.isArray(titleOrg.json_data.settings.hero)) {
            titleOrg.json_data.settings.hero = [titleOrg.json_data.settings.hero]
        }

        this.organization = titleOrg;
    }

    getContentSources = function* (organization_id = this.organization.id) {
        try {
            const obj = yield this.fetchClient("organizations", "organizations_content_sources_list", {
                "organization_pk": organization_id,
            });
            if (obj?.data) {
                const data = JSON.parse(obj.data);
                return this.content_sources = data.data;
            }
        } catch (error) {
            console.debug(error);
        }
    }

    getContentSource = function* (id) {
        try {
            const obj = yield this.fetchClient("organizations", "organizations_content_sources_read", {
                "organization_pk": this.organization.id,
                "id": id,
            });
            if (obj?.data) {
                const data = JSON.parse(obj.data);
                this.content_sources = this.content_sources.map(source => {
                    if (source.id === data.id) {
                        return data;
                    }
                    return source;
                });
            }
        } catch (error) {
            console.debug(error);
        }
    }

    // TODO: need to evaluate the best way to handle the "current" organization ID.  My understanding
    //   is that it should be currentOrganization.id always.
    getContentAnalysis = function* (orgId = this.currentOrganization?.id || this.organization.id) {
        try {
            const obj = yield this.fetchClient("organizations", "organizations_content_analytics_list", {
                organization_pk: orgId,
                'cache-bust': Date.now(),
            }, {organization_id: orgId});
            if (obj?.data) {
                const data = JSON.parse(obj.data);
                const user_permissions = data.user_permissions;
                if (user_permissions && orgId === this.currentOrganization?.id || this.organization.id) {
                    delete data.user_permissions.follower; // follower doesn't cascade - i.e. a follower of the title org is not necessary a follower of a sub-org, so it shouldn't be set here
                    delete data.user_permissions; // remove permissions from obj so it's not passed into content_analytics
                }
                this.content_analytics = data;
                return user_permissions;
            }
        } catch (error) {
            console.debug(error);
        }
    }

    getChildOrganizations = function* () {
        try {
            const item = yield this.fetchClient("organizations", "organizations_list", {
                parent: this.organization.id,
                type__in: ["section", "department", "collection"],
                omit: ["url"],
            });
            if (item?.data) {
                return this.children = JSON.parse(item.data).data;
            }
        } catch (error) {
            console.debug(error);
        }
    }

    createContentSource = async (dataObj) => {
        try {
            const obj = await this.fetchClient("organizations", "organizations_content_sources_create", {
                "organization_pk": this.organization.id,
                data: {
                    ...dataObj,
                    "is_primary": false,
                    "refresh_rate": "01:00:00",
                    "organization": this.organization.id,
                },
            }, {
                requestId: "createContentSource",
                requestSuccessMessage: "Beep, boop... all done! Your new feed will appear below shortly!",
            });
            if (obj?.data) {
                return JSON.parse(obj.data);
            }
        } catch (error) {
            console.debug(error);
        }
    };

    updateSettings = function* (update, showNotification = true) {
        try {
            const result = yield this.fetchClient("organizations", "organizations_settings_partial_update", {
                "organization_pk": this.organization.id,
                data: {...update},
            }, {
                requestId: showNotification ? "updateSettings": null,
            });
            if (result?.data) {
                const settings = {
                    ...this.organization.json_data.settings,
                    ...JSON.parse(result.data).settings,
                };
                this.organization.json_data.settings = settings;
                return settings;
            }
        } catch (error) {
            console.debug(error);
        }
    }

    updateOrganizationField = function* (field, update, showNotification = true, org_id = this.organization.id) {
        try {
            const org = yield this.fetchClient("organizations", "organizations_partial_update", {
                "id": org_id,
                data: {
                    [field]: update,
                },
            }, {
                organization_id: org_id,
                requestId: showNotification ? "updateOrganizationField": null,
            });
            if (org?.data) {
                return this.organization = {...JSON.parse(org.data), ...this.organization};
            }
        } catch (error) {
            console.debug(error);
        }
    }

    refreshContentSource = async id => {
        try {
            await this.fetchClient("content-sources", "content_sources_refresh_create", {
                contentsource_pk: id,
            });
        } catch (error) {
            console.debug(error);
        }
    };

    getUserSites = function* () {
        try {
            const obj = yield this.fetchClient("users", "users_permissions_list", {
                'user_pk': this.userId,
            });
            if (obj?.data) {
                return this.userPermissions = JSON.parse(obj.data);
            }
        } catch (error) {
            console.debug(error);
        }
    }

    imageUpload = function* (attribute, file, name) { // type == 'logo', 'hero', or 'picture'
        try {
            const obj = yield this.fetchClient("organizations", "organizations_file_upload_update", {
                organization_pk: this.organization.id,
                name,
                file: file,
                mime_type: file.type,
                attribute,
            });
            if (obj?.data) {
                const media = JSON.parse(obj.data);

                if (attribute === 'hero') {
                    this.organization.json_data.settings.hero.splice(0, 0, media)
                }

                return media;
            }
        } catch (error) {
            console.debug(error);
        }
    }

    deleteMedia = function* (file_id, attribute) {
        try {
            yield this.fetchClient("organizations", "organizations_file_upload_delete", {
                organization_pk: this.organization.id,
                file_id,
                attribute,
            });

            if (attribute === 'hero') {
                this.organization.json_data.settings.hero = this.organization.json_data.settings.hero.filter(h => h.id !== file_id);
            }
        } catch (error) {
            console.debug(error);
        }
    }

    pollSource = async id => {
        try {
            const obj = await this.fetchClient('content-sources', "content_sources_states_list", {
                contentsource_pk: id,
            });
            if (obj?.data) {
                return JSON.parse(obj.data);
            }
        } catch (error) {
            console.debug(error);
        }
    };

    pollOrganization = async id => {
        try {
            const obj = await this.fetchClient("organizations", "organizations_states_list", {
                organization_pk: id,
            });
            if (obj?.data) {
                return JSON.parse(obj.data);
            }
        } catch (error) {
            console.debug(error);
        }
    };

    getHostname = function* () {
        try {
            const obj = yield this.fetchClient("organizations", "organizations_hostnames_list", {
                organization_pk: this.organization.id,
            });
            if (obj?.data) {
                this.hostname = JSON.parse(obj.data).data;
                return this.hostname;
            }
        } catch (error) {
            console.debug(error);
        }
    }

    patchHostname = function* (id, params, organization_id = this.organization.id) {
        try {
            const obj = yield this.fetchClient("organizations", "organizations_hostnames_partial_update", {
                organization_pk: organization_id,
                id: id,
                data: {...params},
            }, {
                organization_id,
                requestId: "patchHostname",
            });
            if (obj?.data) {
                this.hostname = JSON.parse(obj.data).data;
            }
        } catch (error) {
            console.debug(error);
        }
    }

    createHostname = async (hostname, schema = 'https', organization = this.organization.id) => {
        try {
            const obj = await this.fetchClient("organizations", "organizations_hostnames_create", {
                organization_pk: this.organization.id,
                data: {
                    scheme: schema,
                    hostname: hostname,
                    is_primary: true,
                    is_internal_primary: false,
                    organization: organization,
                },
            }, {organization_id: organization});
            if (obj?.data) {
                return JSON.parse(obj.data);
            }
        } catch (error) {
            console.debug(error);
        }
    };

    bulkUpdateHostnames = async (data, organization_id = this.organization.id) => {
        try {
            const obj = await this.fetchClient("hostnames", "hostnames_bulk_partial_update", {
                data: data,
            }, {organization_id});
            if (obj?.data) {
                return JSON.parse(obj.data);
            }
        } catch (error) {
            console.debug(error);
        }
    }
}
